import { Injectable } from '@angular/core';
import { TranslationService } from 'src/app/core/translation/translation.service';
import { SortChangeEvent, SortMenuItem } from '../components/sort-menu/sort-menu.component';
import { createSorter } from '../utility/sort-utils';

/**
 * Generic sort mode enum that can be used by both Tasks and Jobs
 */
export enum ItemSortMode {
    DATE_CREATED = 0,
    DATE_MODIFIED = 1,
    NAME = 2
}

@Injectable({
    providedIn: 'root'
})
export class ItemSortService {
    constructor(private translate: TranslationService) {}

    /**
     * Get the sort menu items for tasks, jobs, or other items
     * @param dateCreatedLabel - Translation key for date created
     * @param dateModifiedLabel - Translation key for date modified
     * @param nameLabel - Translation key for name
     * @returns Array of sort menu items
     */
    getSortMenuItems<T>(
        dateCreatedField: keyof T = 'createdOn' as keyof T,
        dateModifiedField: keyof T = 'modifiedOn' as keyof T,
        nameField: keyof T = 'title' as keyof T,
        dateCreatedLabel: string = 'TC.Common.DateCreated',
        dateModifiedLabel: string = 'TC.Common.DateModified',
        nameLabel: string = 'TC.Common.Name'
    ): SortMenuItem<T>[] {
        return [
            {
                name: this.translate.instant(dateCreatedLabel),
                id: String(ItemSortMode.DATE_CREATED),
                attribute: dateCreatedField,
                type: 'date',
                className: 'sort-option-date'
            },
            {
                name: this.translate.instant(dateModifiedLabel),
                id: String(ItemSortMode.DATE_MODIFIED),
                attribute: dateModifiedField,
                type: 'date',
                className: 'sort-option-date active-sort'
            },
            {
                name: this.translate.instant(nameLabel),
                id: String(ItemSortMode.NAME),
                attribute: nameField,
                type: 'string',
                className: 'sort-option-name'
            }
        ];
    }

    /**
     * Sort an array of items by the given sort mode and direction
     * @param items - The items to sort
     * @param currentSort - The current sort settings
     * @returns A sorted array of items
     */
    sortItems<T>(items: T[], currentSort: SortChangeEvent<T>): T[] {
        return [...items].sort(
            createSorter<T>(currentSort.sortMode.attribute, currentSort.sortMode.type, currentSort.direction)
        );
    }
}
